import { ColumnDef, Row } from "@tanstack/react-table";
import { MyLead } from "../schema";
import { axiosInstance } from "@/config/axiosInstance";
import Cookies from "js-cookie";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { LeadLog } from "./lead-logs-table/schema";
import { getColumns } from "./lead-logs-table/columns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog/dialog";
import { DataTable } from "./lead-logs-table/data-table";
import { Button } from "@/components/ui/button/button";

type StatusObject = {
  createdBy?: string;
  id: string;
  beforeStatus: string;
  afterStatus: string;
  comments: string;
  createdAt: string | Date;
};

function getLatestObject(objects: StatusObject[]): StatusObject | undefined {
  if (objects.length === 0) return undefined;

  return objects.reduce((latest, current) => {
    const latestDate = new Date(latest.createdAt);
    const currentDate = new Date(current.createdAt);
    return currentDate > latestDate ? current : latest;
  });
}

export const fetchSingleLeadLog = (key: string) => (leadId: string) =>
  axiosInstance
    .get<{
      success: boolean;
      error?: string;
      logs: {
        _id: string;
        beforeStatus: string;
        comments: string;
        afterStatus: string;
        createdAt: string | Date;
        createdBy?: {
          _id: string;
          name: string;
        };
      }[];
    }>(`/lead/logs/${leadId}`, {
      headers: {
        authorization: `Bearer ${key}`,
        "Content-Type": "application/json",
      },
    })
    .then((response) => response.data);

interface LeadLatestCommentProps<TData> {
  row: Row<TData>;
}

export function LeadLatestComment<TData>({
  row,
}: LeadLatestCommentProps<TData>) {
  const key = Cookies.get("key") as string;

  const [open, setOpen] = useState(false);

  const originData = row.original as MyLead;

  const { data, isPending, isFetching } = useQuery({
    queryKey: ["lead-logs", originData.id],
    queryFn: () => fetchSingleLeadLog(key)(originData.id),
  });

  const leadLogs = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (data === undefined || data?.logs === undefined) {
      return [];
    }

    const result = data?.logs?.map((log) => {
      return {
        id: log?._id,
        beforeStatus: log?.beforeStatus,
        afterStatus: log?.afterStatus,
        comments: log?.comments,
        createdAt: log?.createdAt as string | Date,
        ...(log?.createdBy
          ? {
              createdBy: log?.createdBy?.name,
            }
          : {}),
      };
    });

    return result?.sort((a, b) => {
      return (
        new Date(b?.createdAt as string).getTime() -
        new Date(a?.createdAt as string).getTime()
      );
    });
  }, [data, isPending, isFetching]);

  const latest = getLatestObject(leadLogs);

  const columns = useMemo<ColumnDef<LeadLog, unknown>[]>(
    () => getColumns(),
    []
  );

  return (
    <div className="flex items-center gap-2 justify-start">
      {latest && <div>{latest?.comments}</div>}

      <>
        <Dialog
          open={open}
          onOpenChange={(open) => {
            setOpen(open);
          }}
        >
          <DialogTrigger className="self-end">
            <Button size="sm" className="self-end">All</Button>
          </DialogTrigger>
          <DialogContent className="max-w-5xl">
            <DialogHeader>
              <DialogTitle className="tracking-normal">Lead Logs</DialogTitle>
            </DialogHeader>

            <div className="">
              <DataTable data={leadLogs} columns={columns} />
            </div>
          </DialogContent>
        </Dialog>
      </>
    </div>
  );
}
